import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import * as tcEventActions from 'features/tc-event-dashboard/duck';
import * as staticActions from 'shared/ducks/staticDuck';
import * as roomActions from 'features/room-builder/duck';
import * as taskActions from 'features/task-management/duck';
import { getRoomDTO } from 'features/task-management/selectors';
import {
  AlertMessage,
  AlertNotice,
  CheckboxList,
  SimpleModal,
  Textarea,
  ModalInfo,
} from 'shared/components';
import { EditRoomType } from 'features/tc-event-dashboard';
import { PermissionsService } from 'shared/services';
import { find, forEach, head, isEmpty, remove, isEqual } from 'lodash';
import moment from 'moment';
import { DATE_FORMATS, IRREGULARITIES_NOTES_MAX_LENGTH, ROOM_STATUS } from 'shared/constants';
import { isRoomOnline, hasRoomMaterialsRequired, getRoomTimingName } from 'shared/helpers';
import { getManualTimerFlags } from '../selectors';
import QuestionCircle from 'assets/svgs/questionCircle.svg';
import ArrowRight from 'assets/svgs/arrowRight24.svg';

import './TcEventManualTiming.scss';

const locationsToChecklist = (locations, manualTimerFlags, checked, t) =>
  locations.map((location) => ({
    id: location.id,
    area: location.area,
    name: location.name,
    rooms: location.rooms
      ? location.rooms.map((item) => ({
          key: item.id,
          label: `${item.name} ${setLabel(
            manualTimerFlags[item.id],
            item.status,
            item.roomType,
            item.timing,
            t
          )}`,
          checked: isRoomManualTimerDisabled(item, manualTimerFlags) ? false : checked,
          disabled: isRoomManualTimerDisabled(item, manualTimerFlags),
          roomType: item.roomType,
          timing: item.timing,
          status: item.status,
          roomName: item.name,
          singleRoomFlag: item.singleRoomFlag,
          location: {
            area: location.area,
            name: location.name,
          },
        }))
      : [],
  }));

const preCheckedRoom = (room, roomStatus) => [
  {
    area: roomStatus.locationArea,
    name: roomStatus.locationName,
    rooms: [
      {
        key: room.id,
        checked: true,
        roomType: room.roomType,
        timing: room.timing,
        status: room.status,
        roomName: room.name,
        singleRoomFlag: room.singleRoomFlag,
        location: {
          area: roomStatus.locationArea,
          name: roomStatus.locationName,
        },
      },
    ],
  },
];

const isRoomManualTimerDisabled = (item, manualTimerFlags) =>
  manualTimerFlags[item.id].manualTimerFlag ||
  isRoomOnline(item.roomType) ||
  (!manualTimerFlags[item.id].roomMaterialCount && hasRoomMaterialsRequired(item.roomType)) ||
  !item.roomType ||
  (item.roomType && item.roomType.timingSupportedFlag && !item.timing);

const setLabel = (manualTimerFlags, status, roomType, timing, t) => {
  if (manualTimerFlags.manualTimerFlag) {
    return `<span class="manual-timer-label">${t(
      'testCenter.manualTimer.manualTimerOnLabel'
    )}</span>`;
  } else if (status === ROOM_STATUS.closed) {
    return `<span class="manual-timer-label">${t('common.labels.closed')}</span>`;
  } else if (isRoomOnline(roomType)) {
    return `<span class="manual-timer-label">${t('testCenter.manualTimer.onlineLabel')}</span>`;
  } else if (
    (!manualTimerFlags.roomMaterialCount && hasRoomMaterialsRequired(roomType)) ||
    !roomType
  ) {
    return `<span class="manual-timer-label" data-bs-toggle="popover" data-bs-placement="top" data-bs-trigger="hover" data-bs-boundary="window" data-bs-content="${t(
      'testCenter.manualTimer.noMaterialsTooltip'
    )}" onclick="(e)=>bootstrap.Popover.getOrCreateInstance(e.target.id).enable()">
            ${t('testCenter.manualTimer.noMaterialsLabel')}</span>`;
  } else if (roomType && roomType.timingSupportedFlag && !timing) {
    return `<span class="manual-timer-label" data-bs-toggle="popover" data-bs-placement="top" data-bs-trigger="hover" data-bs-boundary="window" data-bs-content="${t(
      'testCenter.manualTimer.noTimingTooltip'
    )}">${t('testCenter.manualTimer.noTimingLabel')}</span>`;
  }
  return '';
};

const getCheckedRooms = (locations) => {
  let data = [];
  locations.forEach(
    (location) =>
      location.rooms &&
      location.rooms.forEach((room) => {
        if (room.checked) {
          data.push({
            roomId: room.key,
          });
        }
      })
  );

  return data;
};

class TcEventManualTiming extends Component {
  constructor(props) {
    super(props);
    this.state = {
      errors: {},
      irregularityDescription: '',
      isOpenIrregularityModal: false,
      isOpenRoomsModal: false,
      isOpenRoomsTypeModal: false,
      isOpenEditRoom: false,
      locations: [],
      showWarningModal: false,
      showConfirmModal: false,
      timingWarningModalOpen: false,
    };

    this.modalStateNames = ['isOpenRoomsModal', 'isOpenIrregularityModal', 'isOpenRoomsTypeModal'];

    this.toggleModal = this.toggleModal.bind(this);
    this.goBack = this.goBack.bind(this);
    this.handleConfirmModal = this.handleConfirmModal.bind(this);
    this.onUpdateRooms = this.onUpdateRooms.bind(this);
    this.selectAllRooms = this.selectAllRooms.bind(this);
    this.handleContinueRooms = this.handleContinueRooms.bind(this);
    this.handleContinueRoomsType = this.handleContinueRoomsType.bind(this);
    this.onChangeIrregularityDescription = this.onChangeIrregularityDescription.bind(this);
    this.handleContinueIrregularity = this.handleContinueIrregularity.bind(this);
    this.removeMessage = this.removeMessage.bind(this);
    this.disableManualTimerAction = this.disableManualTimerAction.bind(this);
    this.onSaveRoomType = this.onSaveRoomType.bind(this);
    this.openEditRoomType = this.openEditRoomType.bind(this);
    this.updateRoomsType = this.updateRoomsType.bind(this);
    this.closeWarningModal = this.closeWarningModal.bind(this);
    this.hasTimingChanged = this.hasTimingChanged.bind(this);
  }

  componentDidMount() {
    const { actions, manualTimerFlags, roomTypes, tcEvent } = this.props;

    if (
      PermissionsService.hasTestCenterManage(tcEvent.testCenterCycleId) ||
      PermissionsService.hasEventManage(tcEvent.id) ||
      PermissionsService.hasRoomAccess(tcEvent.id)
    ) {
      if (isEmpty(manualTimerFlags) && !isEmpty(tcEvent)) {
        actions.getRoomsDTO(tcEvent.id);
      }
    }

    if ((!roomTypes || !roomTypes.length) && !isEmpty(tcEvent) && !this.props.loadingRoomTypes) {
      actions.getRoomTypes(tcEvent.id);
    }

    if (isEmpty(this.props.rooms) && !isEmpty(this.props.room)) {
      remove(this.modalStateNames, (name) => name === 'isOpenRoomsModal');
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      !isEmpty(this.props.locations) &&
      !isEmpty(this.props.manualTimerFlags) &&
      (!isEqual(this.props.locations, prevProps.locations) ||
        !isEqual(this.props.manualTimerFlags, prevProps.manualTimerFlags))
    ) {
      this.setState({
        locations: locationsToChecklist(
          this.props.locations,
          this.props.manualTimerFlags,
          false,
          this.props.t
        ),
      });
    }
    if (
      (!this.props.roomTypes || !this.props.roomTypes.length) &&
      !isEmpty(this.props.tcEvent) &&
      !this.props.loadingRoomTypes
    ) {
      if (prevState.input !== this.state.input) {
        this.props.actions.getRoomTypes(this.props.tcEvent.id);
      }
    }
    const isDisabledManualTimer = this.disableManualTimerAction();

    Array.from(document.querySelectorAll('.manual-timing-info')).forEach((el) => {
      const popover = bootstrap.Popover.getOrCreateInstance(el);
      if (isDisabledManualTimer) {
        popover.enable();
      } else {
        popover.disable();
      }
    });
    Array.from(document.querySelectorAll('.manual-timer-label')).forEach((el) =>
      bootstrap.Popover.getOrCreateInstance(el).enable()
    );
  }

  toggleModal(modal) {
    const { locations, manualTimerFlags, room, roomStatus } = this.props;

    if (isEmpty(modal)) {
      modal = head(this.modalStateNames);
    }
    this.setState((prevState) => ({
      [modal]: !prevState[modal],
    }));

    if (modal === head(this.modalStateNames)) {
      this.setState({
        errors: {},
        locations: !isEmpty(room)
          ? preCheckedRoom(room, roomStatus)
          : locationsToChecklist(locations, manualTimerFlags, false, this.props.t),
        irregularityDescription: '',
      });
    }
  }

  goBack(currentModal) {
    const index = this.modalStateNames.indexOf(currentModal);
    if (index > 0) {
      const backModal = this.modalStateNames[index - 1];
      this.setState({
        [currentModal]: false,
        [backModal]: true,
      });
    } else {
      this.setState({
        [currentModal]: false,
      });
    }
  }

  onUpdateRooms(e) {
    const { locations } = this.state;
    locations.forEach(
      (location) =>
        location.rooms &&
        location.rooms.forEach((room) => {
          if (room.key === +e.target.name) {
            room.checked = e.target.checked;
          }
        })
    );

    this.setState({
      locations,
    });
  }

  selectAllRooms() {
    const { locations, manualTimerFlags } = this.props;

    this.setState({
      errors: {},
      showWarningModal: false,
      locations: locationsToChecklist(locations, manualTimerFlags, true, this.props.t),
    });
  }

  handleContinueRooms() {
    const { locations } = this.state;
    let emptyRoomList = true;
    locations.forEach(
      (location) =>
        location.rooms &&
        location.rooms.forEach((room) => {
          if (room.checked) {
            emptyRoomList = false;
          }
        })
    );

    if (emptyRoomList) {
      this.setState({
        errors: {
          rooms: this.props.t('testCenter.manualTimer.rooms.error'),
        },
      });
    } else {
      this.setState({
        isOpenRoomsModal: false,
        isOpenIrregularityModal: true,
        errors: {},
      });
    }
  }

  onChangeIrregularityDescription(e) {
    const { value } = e.target;

    this.setState({
      irregularityDescription: value,
    });
  }

  handleContinueIrregularity() {
    const { irregularityDescription } = this.state;
    const { room } = this.props;

    if (!isEmpty(irregularityDescription)) {
      const stateData = isEmpty(room)
        ? {
            isOpenRoomsTypeModal: true,
          }
        : {
            showConfirmModal: true,
          };
      this.setState({
        ...stateData,
        isOpenIrregularityModal: false,
        errors: {},
      });
    } else {
      this.setState({
        errors: {
          irregularityDescription: this.props.t('testCenter.manualTimer.irregularity.error'),
        },
      });
    }
  }

  handleContinueRoomsType() {
    const { locations } = this.state;
    let typeChooseLaterExists = false;
    locations.forEach(
      (location) =>
        location.rooms &&
        location.rooms.forEach((room) => {
          if (room.checked && !room.roomType) {
            typeChooseLaterExists = true;
          }
        })
    );

    if (typeChooseLaterExists) {
      this.setState({
        errors: {
          roomsType: this.props.t('testCenter.manualTimer.roomType.error'),
        },
      });
    } else {
      this.setState({
        isOpenRoomsTypeModal: false,
        showConfirmModal: true,
        errors: {},
      });
    }
  }

  openEditRoomType(room) {
    this.setState({
      editRoom: room,
      isOpenEditRoom: true,
      errors: {},
    });
  }

  hasTimingChanged(editedRoom) {
    const { rooms } = this.props;
    const notUpdatedRoom = rooms.find((r) => r.id === editedRoom.key);

    return notUpdatedRoom.timing !== editedRoom.timing;
  }

  onSaveRoomType(editedRoom, checkTimingChange) {
    const { locations } = this.state;
    const { roomTypes } = this.props;

    if (checkTimingChange && this.hasTimingChanged(editedRoom)) {
      this.setState({
        timingWarningModalOpen: true,
      });
      return;
    }

    let locationsUpdated = Object.assign([], locations);

    forEach(locationsUpdated, (location) => {
      location.rooms &&
        location.rooms.map((room) => {
          if (room.key === editedRoom.key) {
            room.isDirty = true;
            room.timing = editedRoom.timing;
            room.roomType = find(roomTypes, {
              id: parseInt(editedRoom.roomType),
            });
          }
        });
    });

    this.setState({
      locations: locationsUpdated,
      isOpenEditRoom: false,
      timingWarningModalOpen: false,
    });
  }

  async handleConfirmModal() {
    const { irregularityDescription, locations } = this.state;

    const { actions, currentUser, tcEvent, room, roomActions, taskActions } = this.props;
    const irregularityTypes = await actions.getIrregularityTypes({
      cycleId: tcEvent.testingEvent.adminId.cycleId.id,
    });

    const manualTimerType = find(irregularityTypes, { type: 'timer_manual' });
    await actions.setRoomManualTimer({
      testCenterEventId: tcEvent.id,
      irregularityType: {
        id: manualTimerType?.id,
      },
      scope: 'room',
      notes: irregularityDescription,
      startDateTime: moment().format(DATE_FORMATS.dateTimeZone),
      duration: 1,
      submitPersonnel: {
        id: currentUser.id,
      },
      submitDateTime: moment().format(DATE_FORMATS.dateTimeZone),
      irregularityExaminees: [],
      irregularityRooms: getCheckedRooms(locations),
      irregularityStages: [],
      reviewCompleteFlag: false,
    });
    if (room?.status === ROOM_STATUS.inactive || room?.status === ROOM_STATUS.closed) {
      await roomActions.updateRoomStatus(room.id, {
        status: ROOM_STATUS.opened,
      });
    }

    if (!isEmpty(room)) {
      taskActions.getRoomCompletedTasks(room.id);
      taskActions.getRoomTimers(room.id);
      await roomActions.getRoom(room.id);
      await taskActions.getRoomStatus(room.id);
      if (
        PermissionsService.hasTestCenterManage(tcEvent.testCenterCycleId) ||
        PermissionsService.hasEventManage(tcEvent.id) ||
        PermissionsService.hasRoomAccess(tcEvent.id)
      ) {
        actions.getRoomsDTO(tcEvent.id);
      } else {
        roomActions.getRoomDTO(room.id);
      }
    } else {
      this.updateRoomsType();
      actions.getRoomsDTO(tcEvent.id);
    }

    this.setState({
      showConfirmModal: false,
      showMessage: true,
      showMessageTime: 3000,
    });
  }

  async updateRoomsType() {
    const { actions, roomActions, tcEvent } = this.props;
    const { locations } = this.state;

    await forEach(
      locations,
      async (location) =>
        location.rooms &&
        forEach(location.rooms, async (room) => {
          if (room.checked) {
            if (room.isDirty) {
              await roomActions.updateRoomTypeTiming(room.key, {
                timing: room.timing,
                type: {
                  id: room.roomType.id,
                },
              });
            }
            if (room.status === ROOM_STATUS.inactive || room.status === ROOM_STATUS.closed) {
              await roomActions.updateRoomStatus(room.key, {
                status: ROOM_STATUS.opened,
              });
            }
          }
        })
    );
    await actions.getRooms(tcEvent.id);
  }

  removeMessage() {
    this.setState({
      showMessage: false,
    });
  }

  disableManualTimerAction() {
    const { locations, manualTimerFlags, room, roomDTO } = this.props;
    let disable = true;

    if (!isEmpty(room)) {
      if (!isEmpty(roomDTO) && !roomDTO.manualTimerFlag && !isRoomOnline(room.roomType)) {
        disable = false;
      }
    } else {
      locations.forEach(
        (location) =>
          location.rooms &&
          location.rooms.forEach((room) => {
            if (
              !isEmpty(manualTimerFlags[room.id]) &&
              !manualTimerFlags[room.id].manualTimerFlag &&
              !isRoomOnline(room.roomType)
            ) {
              disable = false;
            }
          })
      );
    }

    return disable;
  }

  closeWarningModal() {
    this.setState({
      timingWarningModalOpen: false,
    });
  }

  render() {
    const {
      errors,
      irregularityDescription,
      isOpenIrregularityModal,
      isOpenRoomsModal,
      isOpenRoomsTypeModal,
      isOpenEditRoom,
      locations,
      showWarningModal,
      showConfirmModal,
      editRoom,
      timingWarningModalOpen,
    } = this.state;
    const { room } = this.props;

    const isDisabledManualTimer = this.disableManualTimerAction();

    return room && isRoomOnline(room.roomType) ? null : (
      <div className="manual-timing">
        {room && room.id ? (
          <div
            className={`room-manual-timing-info ${isDisabledManualTimer ? 'disabled' : null}`}
            data-bs-toggle="popover"
            data-bs-placement="top"
            data-bs-container="body"
            data-bs-content={
              isDisabledManualTimer ? this.props.t('testCenter.manualTimer.disabledMessage') : ''
            }>
            <p>{this.props.t('testCenter.manualTimer.dashboardMessage')}</p>
            <div className="display-flex action-section">
              <div>
                <span
                  className={`btn-link icon-link ${isDisabledManualTimer ? 'disabled' : ''}`}
                  onClick={() => (isDisabledManualTimer ? null : this.toggleModal())}>
                  <ArrowRight />
                  {this.props.t('testCenter.manualTimer.buttonLabel')}
                </span>
              </div>
            </div>
          </div>
        ) : (
          <div className="d-flex">
            <div
              className={`manual-timing-info ${isDisabledManualTimer ? 'disabled' : null}`}
              data-bs-toggle="popover"
              data-bs-placement="top"
              data-bs-trigger="hover"
              data-bs-container="body"
              tabIndex="0"
              data-bs-content={
                isDisabledManualTimer ? this.props.t('testCenter.manualTimer.disabledMessage') : ''
              }>
              <div
                className={`btn-link ${isDisabledManualTimer ? 'disabled' : ''}`}
                onClick={() => (isDisabledManualTimer ? null : this.toggleModal())}>
                {this.props.t('testCenter.manualTimer.buttonLabel')}
              </div>
            </div>
            <div
              className={`${isDisabledManualTimer ? 'disabled' : ''} info-icon`}
              data-bs-toggle="popover"
              data-bs-placement="top"
              data-bs-trigger="hover"
              tabIndex="0"
              data-bs-content={this.props.t('testCenter.manualTimer.dashboardMessage')}>
              <QuestionCircle />
            </div>
          </div>
        )}

        <SimpleModal
          isOpen={showConfirmModal}
          icon="error_outline"
          onCancel={() => this.toggleModal('showConfirmModal')}
          onConfirm={this.handleConfirmModal}
          message={this.props.t('testCenter.manualTimer.confirmMessage')}
          confirmLabel={this.props.t('common.labels.yes')}
          cancelLabel={this.props.t('common.labels.no')}
          headline={this.props.t('testCenter.manualTimer.confirmTitle')}
        />

        <ModalInfo
          modalIsOpen={isOpenRoomsModal}
          closeModal={() => this.toggleModal('isOpenRoomsModal')}
          headerTitle={this.props.t('testCenter.manualTimer.manualTimerLabel')}
          onSubmit={this.handleContinueRooms}
          saveLabel={this.props.t('testCenter.manualTimer.rooms.continueButton')}>
          <AlertNotice
            message={this.props.t('testCenter.manualTimer.warningInfo')}
            type="warning"
          />
          <div>
            {errors.rooms && <AlertNotice message={errors.rooms} type="error" />}
            {locations.length ? (
              <div>
                {locations.map((location) => (
                  <div key={`eventLocation-${location.id}`} className="mb-3">
                    {location.rooms && location.rooms.length ? (
                      <CheckboxList
                        checkboxes={location.rooms}
                        onChange={this.onUpdateRooms}
                        title={`${location.name}${
                          location.area && location.area.length > 0 && `, ${location.area}`
                        }`}
                      />
                    ) : (
                      <div>
                        <h6 className="title">
                          {' '}
                          {location.name}
                          {location.area && location.area.length > 0 && `, ${location.area}`}
                        </h6>
                        <AlertNotice message={this.props.t('roomBuilder.noRooms')} inline={true} />
                      </div>
                    )}
                  </div>
                ))}
                <div
                  className="select-all-rooms"
                  onClick={() => this.toggleModal('showWarningModal')}>
                  {this.props.t('testCenter.manualTimer.rooms.selectAllLabel')}
                </div>
              </div>
            ) : (
              <AlertNotice message={this.props.t('testCenter.eventDashboard.noRoomsLabel')} />
            )}
          </div>
        </ModalInfo>

        <SimpleModal
          isOpen={showWarningModal}
          icon="error_outline"
          onCancel={() => this.toggleModal('showWarningModal')}
          onConfirm={this.selectAllRooms}
          message={this.props.t('testCenter.manualTimer.warningMessage')}
          confirmLabel={this.props.t('common.labels.yes')}
          cancelLabel={this.props.t('common.labels.no')}
          headline={this.props.t('testCenter.manualTimer.warningTitle')}
        />

        <ModalInfo
          modalIsOpen={isOpenIrregularityModal}
          closeModal={() => this.toggleModal('isOpenIrregularityModal')}
          headerTitle={this.props.t('testCenter.manualTimer.manualTimerLabel')}
          onSubmit={this.handleContinueIrregularity}
          saveLabel={this.props.t('common.labels.continue')}
          showCancel
          cancelLabel={this.props.t('common.labels.back')}
          onCancel={() => this.goBack('isOpenIrregularityModal')}>
          <AlertNotice
            message={this.props.t('testCenter.manualTimer.warningInfo')}
            type="warning"
          />
          {isEmpty(room) && (
            <React.Fragment>
              <div className="text-strong">
                {this.props.t('testCenter.manualTimer.irregularity.selectedRoomsLabel')}
              </div>
              {locations.map(
                (location) =>
                  location.rooms &&
                  location.rooms.map(
                    (room) =>
                      room.checked && (
                        <div key={room.key} className="room-name">
                          {room.roomName}, {location.name}
                          {location.area && location.area.length > 0 && `, ${location.area}`}
                        </div>
                      )
                  )
              )}
            </React.Fragment>
          )}
          <Textarea
            className="mb-1 mt-1"
            label={this.props.t('testCenter.manualTimer.irregularity.inputLabel')}
            name="irregularityDescription"
            value={irregularityDescription}
            onChange={this.onChangeIrregularityDescription}
            maxLength={IRREGULARITIES_NOTES_MAX_LENGTH}
            error={errors.irregularityDescription}
          />
        </ModalInfo>

        <ModalInfo
          modalIsOpen={isOpenRoomsTypeModal}
          closeModal={() => this.toggleModal('isOpenRoomsTypeModal')}
          headerTitle={this.props.t('testCenter.manualTimer.manualTimerLabel')}
          onSubmit={this.handleContinueRoomsType}
          saveLabel={this.props.t('common.labels.save')}
          showCancel
          cancelLabel={this.props.t('common.labels.back')}
          onCancel={() => this.goBack('isOpenRoomsTypeModal')}>
          <AlertNotice
            message={this.props.t('testCenter.manualTimer.warningInfo')}
            type="warning"
          />
          <p className="mb-30">{this.props.t('testCenter.manualTimer.roomType.infoMessage')}</p>
          <div>
            {locations.length ? (
              <div>
                {locations.map(
                  (location) =>
                    location.rooms &&
                    location.rooms.map(
                      (room) =>
                        room.checked && (
                          <div key={room.key} className="room-type">
                            <div className="room-name">
                              <span>{room.roomName}</span>, {location.name}
                              {location.area && location.area.length > 0 && `, ${location.area}`}
                            </div>
                            {errors.roomsType && !room.roomType && (
                              <div className="error">{errors.roomsType}</div>
                            )}
                            <div className="room-details">
                              <div>
                                <div>
                                  {this.props.t('roomBuilder.roomTypeLabel')}:{' '}
                                  {room.roomType
                                    ? room.roomType.name
                                    : this.props.t('taskManagement.addRoomType')}
                                </div>
                                <div>
                                  {this.props.t('roomBuilder.timingCategoryLabel')}:{' '}
                                  {getRoomTimingName(room.timing, this.props.tcEvent?.testingEvent?.adminId?.cycleId?.programId?.code, this.props.mappedRoomTiming)}
                                </div>
                              </div>
                              <div>
                                <button
                                  className="btn-link"
                                  onClick={() => this.openEditRoomType(room)}
                                  disabled={
                                    room.status !== ROOM_STATUS.inactive ? 'disabled' : null
                                  }>
                                  <i className="fa fa-pencil" aria-hidden="true" /> {` `}
                                  {this.props.t('common.labels.editInfo')}
                                </button>
                              </div>
                            </div>
                          </div>
                        )
                    )
                )}
              </div>
            ) : (
              <AlertNotice message={this.props.t('testCenter.eventDashboard.noRoomsLabel')} />
            )}
          </div>
        </ModalInfo>

        {isOpenEditRoom && (
          <EditRoomType
            closeWarningModal={this.closeWarningModal}
            timingWarningModalOpen={timingWarningModalOpen}
            isOpen={isOpenEditRoom}
            room={editRoom}
            onSave={this.onSaveRoomType}
            onCancel={() => this.toggleModal('isOpenEditRoom')}
            mappedRoomTiming={this.props.mappedRoomTiming}
            programCode={this.props.tcEvent?.testingEvent?.adminId?.cycleId?.programId?.code}
          />
        )}

        <AlertMessage
          show={this.state.showMessage}
          time={this.state.showMessageTime}
          removeMessage={this.removeMessage}
          message={this.props.t('testCenter.manualTimer.successMessage')}
        />
      </div>
    );
  }
}

TcEventManualTiming.propTypes = {
  t: PropTypes.func.isRequired,
};

function mapStateToProps(state) {
  return {
    currentUser: state.users.currentUser,
    tcEvent: state.tcEvent.currentEvent || {},
    manualTimerFlags: getManualTimerFlags(state),
    roomTypes: state.statics.roomTypes,
    loadingRoomTypes: state.statics.loadingRoomTypes,
    roomDTO: getRoomDTO(state),
    mappedRoomTiming: state.statics.mappedRoomTiming,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({ ...tcEventActions, ...staticActions }, dispatch),
    roomActions: bindActionCreators(roomActions, dispatch),
    taskActions: bindActionCreators(taskActions, dispatch),
  };
}

export default withTranslation()(connect(mapStateToProps, mapDispatchToProps)(TcEventManualTiming));
